<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { EcxDrawer, EcxButton } from '@ecx/ui'
import { UserPlusIcon, VehicleAssetSliderVehicleIcon, DocumentDownloadIcon, ExcelFileIcon } from '@ecx/ui/icons'
import { useBulkImportStore } from '@/stores/bulkImport'
import type { BulkImportEntity } from '@/types/bulkImport'
import BulkImportSelectorStep from './steps/BulkImportSelectorStep.vue'
import BulkImportUploadStep from './steps/BulkImportUploadStep.vue'
import BulkImportValidatingStep from './steps/BulkImportValidatingStep.vue'
import BulkImportSummaryStep from './steps/BulkImportSummaryStep.vue'
import BulkImportValidationErrorStep from './steps/BulkImportValidationErrorStep.vue'
import BulkImportProcessingStep from './steps/BulkImportProcessingStep.vue'
import BulkImportResultStep from './steps/BulkImportResultStep.vue'
import BulkImportReportDownloadButton from './shared/BulkImportReportDownloadButton.vue'

const props = withDefaults(
  defineProps<{
    open: boolean
    entity?: BulkImportEntity
    canImport?: boolean
    /** Quando true, fechar o drawer não chama store.reset(), usado ao minimizar. */
    persistOnClose?: boolean
  }>(),
  {
    entity: 'users',
    canImport: true,
    persistOnClose: false,
  },
)

const emit = defineEmits<{
  (e: 'update:open', v: boolean): void
  (e: 'select-manual'): void
  (e: 'completed'): void
  (e: 'navigate-users', importId: string): void
  (e: 'navigate-vehicles', importId: string): void
  (e: 'navigate-cards', importId: string): void
  (e: 'minimize'): void
  (e: 'contact-support'): void
}>()

const { t } = useI18n()
const store = useBulkImportStore()

const isVehicles = computed(() => store.entity === 'vehicles')
const isCards = computed(() => store.entity === 'cards')
const isEdit = computed(() => store.mode === 'edit')

const drawerTitle = computed(() => {
  if (isEdit.value) return t('corp.bulk_import.edit.drawer_title')
  return isVehicles.value
    ? t('corp.bulk_import.drawer_title_vehicles')
    : t('corp.bulk_import.drawer_title')
})

const goToRecordsLabel = computed(() => {
  if (isCards.value) return t('corp.bulk_import.edit.result.go_to_cards')
  return isVehicles.value
    ? t('corp.bulk_import.edit.result.go_to_vehicles')
    : t('corp.bulk_import.edit.result.go_to_users')
})

function goToEditedRecords() {
  const editedId = store.currentImport?.id ?? ''
  if (isCards.value) {
    emit('navigate-cards', editedId)
  } else if (isVehicles.value) {
    emit('navigate-vehicles', editedId)
  } else {
    emit('navigate-users', editedId)
  }
}

function onOpenChange(v: boolean) {
  emit('update:open', v)
}

const hasFooter = computed(() =>
  ['upload', 'summary', 'validation_error', 'processing', 'result_success', 'result_partial'].includes(store.step),
)

const isBusyStep = computed(() => store.step === 'validating' || store.step === 'processing')

watch(
  () => props.open,
  (isOpen) => {
    store.setDrawerOpen(isOpen)
    if (isOpen) {
      if (store.step === 'selector') {
        store.open(props.entity)
      }
    } else {
      if (!props.persistOnClose) {
        store.reset()
      }
    }
  },
  { immediate: true },
)

watch(
  () => store.step,
  (step) => {
    if (step === 'result_success' || step === 'result_partial') {
      emit('completed')
    }
  },
)
</script>

<template>
  <EcxDrawer
    :open="open"
    :title="drawerTitle"
    size="md-fixed"
    side="right"
    content-padding="none"
    :show-close-button="store.step !== 'validating'"
    :close-on-backdrop="!isBusyStep"
    :close-on-esc="!isBusyStep"
    @update:open="onOpenChange"
  >
    <template #icon>
      <ExcelFileIcon v-if="isEdit" class="w-5 h-5 text-royal-500" />
      <component
        v-else
        :is="isVehicles ? VehicleAssetSliderVehicleIcon : UserPlusIcon"
        :class="isVehicles ? 'w-5 h-5 text-royal-300' : 'w-6 h-6 text-royal-500'"
      />
    </template>
    <template v-if="store.step === 'selector'">
      <BulkImportSelectorStep
        :can-import="canImport"
        @select-manual="emit('select-manual')"
      />
    </template>

    <template v-else-if="store.step === 'upload'">
      <BulkImportUploadStep />
    </template>

    <template v-else-if="store.step === 'validating'">
      <BulkImportValidatingStep />
    </template>

    <template v-else-if="store.step === 'summary'">
      <BulkImportSummaryStep />
    </template>

    <template v-else-if="store.step === 'validation_error'">
      <BulkImportValidationErrorStep />
    </template>

    <template v-else-if="store.step === 'processing'">
      <BulkImportProcessingStep />
    </template>

    <template v-else-if="store.step === 'result_success'">
      <BulkImportResultStep
        variant="success"
        @navigate-users="emit('navigate-users', $event)"
        @navigate-vehicles="emit('navigate-vehicles', $event)"
        @navigate-cards="emit('navigate-cards', $event)"
        @contact-support="emit('contact-support')"
      />
    </template>

    <template v-else-if="store.step === 'result_partial'">
      <BulkImportResultStep
        variant="partial"
        @navigate-users="emit('navigate-users', $event)"
        @navigate-vehicles="emit('navigate-vehicles', $event)"
        @navigate-cards="emit('navigate-cards', $event)"
        @contact-support="emit('contact-support')"
      />
    </template>

    <template v-if="hasFooter" #footer>
      <template v-if="store.step === 'upload'">
        <div class="flex items-center justify-between w-full gap-3">
          <EcxButton variant="secondary" @click="onOpenChange(false)">
            {{ t('corp.bulk_import.upload.cancel') }}
          </EcxButton>
          <EcxButton
            variant="primary"
            :disabled="!store.file"
            :loading="store.isUploading || store.isValidating"
            @click="store.analyze()"
          >
            {{ t('corp.bulk_import.upload.analyze') }}
          </EcxButton>
        </div>
      </template>

      <template v-else-if="store.step === 'summary'">
        <div class="flex items-center justify-between w-full gap-3">
          <EcxButton variant="secondary" @click="store.retryUpload()">
            {{ t('corp.bulk_import.summary.back') }}
          </EcxButton>
          <div class="flex items-center gap-3">
            <BulkImportReportDownloadButton
              v-if="store.currentImport?.has_error_report"
              :label="t('corp.bulk_import.summary.download_report')"
              @download="(format) => store.downloadReport('error', format)"
            />
            <EcxButton
              variant="primary"
              :loading="store.isConfirming"
              :disabled="(store.totals?.valid ?? 0) === 0"
              @click="store.confirm()"
            >
              {{ isEdit ? t('corp.bulk_import.edit.summary.confirm') : t('corp.bulk_import.summary.confirm') }}
            </EcxButton>
          </div>
        </div>
      </template>

      <template v-else-if="store.step === 'validation_error'">
        <div class="flex items-center justify-between w-full gap-3">
          <EcxButton variant="secondary" @click="store.retryUpload()">
            {{ t('corp.bulk_import.validation_error.back') }}
          </EcxButton>
          <div class="flex items-center gap-3">
            <!-- errors vazio = planilha com estrutura errada (variante A) -->
            <EcxButton
              v-if="store.errors.length === 0 && !isEdit"
              variant="secondary"
              @click="store.downloadTemplate()"
            >
              <template #iconLeft>
                <DocumentDownloadIcon class="w-4 h-4 text-royal-500" />
              </template>
              {{ t('corp.bulk_import.step_selector.download_template') }}
            </EcxButton>
            <BulkImportReportDownloadButton
              v-else-if="store.currentImport?.has_error_report"
              :label="t('corp.bulk_import.validation_error.download_report')"
              @download="(format) => store.downloadReport('error', format)"
            />
            <EcxButton variant="primary" @click="store.retryUpload()">
              {{ t('corp.bulk_import.validation_error.retry_upload') }}
            </EcxButton>
          </div>
        </div>
      </template>

      <template v-else-if="store.step === 'processing'">
        <div class="flex items-center justify-between w-full gap-3">
          <EcxButton variant="secondary" @click="store.retryUpload()">
            {{ t('corp.bulk_import.processing.back') }}
          </EcxButton>
          <EcxButton variant="outline" @click="emit('minimize')">
            {{ t('corp.bulk_import.processing.minimize') }}
          </EcxButton>
        </div>
      </template>

      <template v-else-if="store.step === 'result_success'">
        <div class="flex items-center justify-between w-full gap-3">
          <EcxButton variant="secondary" @click="onOpenChange(false)">
            {{ t('corp.bulk_import.result_success.back') }}
          </EcxButton>
          <div class="flex items-center gap-3">
            <BulkImportReportDownloadButton
              :label="t('corp.bulk_import.result_success.download_report')"
              @download="(format) => store.downloadReport('result', format)"
            />
            <EcxButton v-if="isEdit" variant="primary" @click="goToEditedRecords()">
              {{ goToRecordsLabel }}
            </EcxButton>
            <EcxButton v-else variant="primary" @click="store.startNewImport()">
              {{ t('corp.bulk_import.result_success.new_import') }}
            </EcxButton>
          </div>
        </div>
      </template>

      <template v-else-if="store.step === 'result_partial'">
        <div class="flex items-center justify-between w-full gap-3">
          <EcxButton variant="secondary" @click="onOpenChange(false)">
            {{ t('corp.bulk_import.result_partial.back') }}
          </EcxButton>
          <div class="flex items-center gap-3">
            <BulkImportReportDownloadButton
              :label="t('corp.bulk_import.result_partial.download_report')"
              @download="(format) => store.downloadReport('error', format)"
            />
            <EcxButton v-if="isEdit" variant="primary" @click="goToEditedRecords()">
              {{ goToRecordsLabel }}
            </EcxButton>
            <EcxButton v-else variant="primary" @click="store.startNewImport()">
              {{ t('corp.bulk_import.result_partial.new_import') }}
            </EcxButton>
          </div>
        </div>
      </template>
    </template>
  </EcxDrawer>
</template>
